/**
 * Les dossiers d'ouverture du mode `?demo` (ARTO-127, ARTO-128) : un dossier
 * fictif crédible dans chaque état, en mémoire, pour l'assistant et la fiche
 * dossier sans API. Aucune vraie galerie : des noms inventés.
 *
 * Les règles que les écrans affichent sont rejouées avec les codes de l'API
 * (`docs/dev/api.md` § 12.14) : révision périmée, dossier clos, transition
 * non disponible ou interdite, contrat incomplet, provisionnement incomplet,
 * état incompatible, dossier incomplet.
 */
import { ApiError } from './api.js';
import { demoApi } from './demoData.js';
import type {
  DisponibiliteSlug,
  DossierOuverture,
  ControleRecette,
  EtapeBascule,
  EtapeCreationOuverture,
  EtapeOuverture,
  EtatOuverture,
  LigneRecette,
  ModificationOuverture,
  PageOuvertures,
  RecetteOuverture,
  ReponseBascule,
  ReponseCreationGalerieOuverture,
  ReponseTransitionOuverture,
  ResumeOuverture,
  SectionsOuverture,
} from '../types.js';

const ETAPES: readonly EtapeCreationOuverture[] = [
  'provisionnement',
  'modules',
  'addons',
  'domaine',
  'invitations',
  'prereglages',
];
const CLOS: readonly EtatOuverture[] = ['en_service', 'abandonne'];
const SUIVANTS: Record<EtatOuverture, readonly EtatOuverture[]> = {
  brouillon: ['contrat_signe', 'provisionne', 'abandonne'],
  contrat_signe: ['provisionne', 'abandonne'],
  provisionne: ['reprise', 'recette', 'abandonne'],
  reprise: ['recette', 'abandonne'],
  recette: ['bascule_planifiee', 'abandonne'],
  bascule_planifiee: ['en_service', 'abandonne'],
  en_service: [],
  abandonne: [],
};
const OPERATEUR = { id: '66f0000000000000000000a1', email: 'exploitation@venio.test' };
const RESERVES = ['app', 'api', 'console', 'demo', 'www', 'conditions'];

/** Comme l'API : ce que `POST …/transition` accepterait maintenant. */
function transitionsPossibles(ligne: DossierOuverture): EtatOuverture[] {
  return SUIVANTS[ligne.etat].filter(
    (vers) =>
      // La mise en service ne passe que par la bascule.
      vers !== 'en_service' &&
      !(ligne.etat === 'brouillon' && vers === 'provisionne' && !ligne.identite.estTest),
  );
}

function jour(decalage: number): string {
  return new Date(Date.now() + decalage * 86_400_000).toISOString().slice(0, 10);
}

function etapesFaites(jusqua: number, le: string): EtapeOuverture[] {
  return ETAPES.slice(0, jusqua).map((cle) => ({
    cle,
    etat: cle === 'addons' || cle === 'domaine' ? 'sans_objet' : 'fait',
    par: OPERATEUR.email,
    le,
    note: cle === 'modules' ? 'coffre, facturation' : '',
  }));
}

function dossierVide(rang: number, nom: string): DossierOuverture {
  const le = new Date().toISOString();
  return {
    id: `66f00000000000000000${String(rang).padStart(4, '0')}`,
    numero: `OUV-2026-${String(rang).padStart(3, '0')}`,
    etat: 'brouillon',
    revision: 0,
    galleryId: null,
    responsable: OPERATEUR,
    identite: {
      nomCommercial: nom,
      slug: '',
      raisonSociale: '',
      formeJuridique: '',
      siren: '',
      siret: '',
      tvaIntracom: '',
      adresse: { ligne1: '', ligne2: '', codePostal: '', ville: '', pays: 'FR' },
      langue: 'fr',
      devise: 'EUR',
      fuseau: 'Europe/Paris',
      estTest: false,
    },
    offre: {
      palier: 'galerie',
      modules: [],
      addons: [],
      services: [],
      groupeSlug: null,
      forfaitReprise: { texte: '', montantCentimes: null },
    },
    contrat: { reference: '', signeLe: null, piece: null },
    equipe: { direction: null, membres: [], invitations: 'maintenant' },
    prereglages: { mentionsComplementaires: [], domaine: null },
    jalons: {
      basculePrevueLe: null,
      basculeEffectueeLe: null,
      scelleLe: null,
      purgeSourcesLe: null,
    },
    etapes: [],
    notes: [],
    transitionsPossibles: ['contrat_signe', 'abandonne'],
    creeLe: le,
    modifieLe: le,
  };
}

function dossier(
  rang: number,
  etat: EtatOuverture,
  nom: string,
  slug: string,
  options: { creee?: boolean; signeLe?: string | null; bascule?: string | null } = {},
): DossierOuverture {
  const le = `2026-09-${String(10 + (rang % 18))}T09:30:00.000Z`;
  const base = dossierVide(rang, nom);
  const ligne: DossierOuverture = {
    ...base,
    etat,
    revision: rang + 2,
    galleryId: options.creee ? `66f10000000000000000${String(rang).padStart(4, '0')}` : null,
    identite: {
      ...base.identite,
      slug,
      raisonSociale: `${nom} SAS`,
      formeJuridique: 'SAS',
      adresse: {
        ligne1: `${String(rang * 7)} rue de Seine`,
        ligne2: '',
        codePostal: '75006',
        ville: 'Paris',
        pays: 'FR',
      },
    },
    offre: {
      ...base.offre,
      modules: ['coffre', 'facturation'],
      services: rang % 2 === 0 ? ['site'] : [],
      forfaitReprise: {
        texte: 'Reprise du registre et du carnet d’adresses',
        montantCentimes: null,
      },
    },
    contrat: {
      reference: etat === 'brouillon' ? '' : `BC-2026-${String(40 + rang)}`,
      signeLe: options.signeLe ?? null,
      piece:
        options.signeLe === undefined || options.signeLe === null
          ? null
          : {
              sha256: 'a3f1c2d4e5b60718293a4b5c6d7e8f90a1b2c3d4e5f60718293a4b5c6d7e8f90',
              octets: 184_320,
              deposeLe: le,
              par: 'direction@venio.test',
            },
    },
    equipe: {
      direction: { nom: 'Direction de la galerie', email: `direction@${slug}.test` },
      membres: [{ nom: 'Régie des œuvres', email: `regie@${slug}.test`, role: 'equipe' }],
      invitations: 'a_la_bascule',
    },
    jalons: {
      ...base.jalons,
      basculePrevueLe: options.bascule ?? null,
      basculeEffectueeLe: etat === 'en_service' ? (options.bascule ?? null) : null,
    },
    etapes: options.creee ? etapesFaites(ETAPES.length, le) : [],
    notes:
      rang === 4
        ? [
            {
              texte: 'Export du registre reçu : trois feuilles, des doublons à écarter.',
              par: OPERATEUR.email,
              le,
            },
          ]
        : [],
    creeLe: le,
    modifieLe: le,
  };
  ligne.transitionsPossibles = transitionsPossibles(ligne);
  return ligne;
}

const dossiers: DossierOuverture[] = [
  dossier(1, 'brouillon', 'Galerie Sillage', 'galerie-sillage'),
  dossier(2, 'contrat_signe', 'Atelier Cinabre', 'atelier-cinabre', { signeLe: '2026-09-22' }),
  dossier(3, 'provisionne', 'Galerie Parallaxe', 'galerie-parallaxe', {
    creee: true,
    signeLe: '2026-09-15',
    bascule: jour(49),
  }),
  dossier(4, 'reprise', 'Galerie Hors-Champ', 'galerie-hors-champ', {
    creee: true,
    signeLe: '2026-09-02',
    bascule: jour(35),
  }),
  dossier(5, 'recette', 'Galerie Aubade', 'galerie-aubade', {
    creee: true,
    signeLe: '2026-08-20',
    bascule: jour(-6),
  }),
  dossier(6, 'bascule_planifiee', 'Galerie Linéaire', 'galerie-lineaire', {
    creee: true,
    signeLe: '2026-08-04',
    bascule: jour(8),
  }),
  dossier(7, 'en_service', 'Galerie Ombre Portée', 'galerie-ombre-portee', {
    creee: true,
    signeLe: '2026-07-10',
    bascule: jour(-13),
  }),
  dossier(8, 'abandonne', 'Galerie Vasarelle', 'galerie-vasarelle'),
];

// ── Recette et bascule (ARTO-134) ──────────────────────────────────────────
//
// Une reprise crédible et cohérente : `ecart` = total − tout le reste, le
// delta laisse quelques fiches à revoir. Trois dossiers montrent trois
// moments : Aubade (recette, pas encore validée, un écart à expliquer),
// Linéaire (bascule planifiée, dont la première tentative s'arrête à l'envoi
// des invitations), Ombre Portée (basculée). Les gestes de l'onglet
// (cases, dérogation, bascule) modifient cet état en mémoire.

function ligneRecette(
  nature: LigneRecette['nature'],
  lots: number,
  total: number,
  crees: number,
  completes: number,
  ignores: number,
  refuses: number,
  ecartes: number,
  aRevoir: number,
  motifs: string[] = [],
): LigneRecette {
  return {
    nature,
    lots,
    total,
    crees,
    completes,
    ignores,
    refuses,
    ecartes,
    ecart: total - (crees + completes + ignores + refuses + ecartes),
    motifs,
    aRevoir,
  };
}

/** `avecEcart` : le tableau d'une recette en cours, où deux natures ne tombent pas juste. */
function naturesRecette(avecEcart: boolean): LigneRecette[] {
  return [
    ligneRecette('lieux', 1, 14, 12, 2, 0, 0, 0, 0),
    ligneRecette('artistes', 1, 86, 79, 4, 3, 0, 0, 0),
    ligneRecette('oeuvres', 3, avecEcart ? 1_252 : 1_248, 1_201, 0, 18, 6, 23, 3, [
      'Doublons du registre : même numéro d’inventaire, fiches fusionnées à la main.',
    ]),
    ligneRecette('images', 2, 1_410, 1_187, 0, 211, 12, 0, 0),
    ligneRecette('localisations', 1, 2_904, 2_890, 0, 0, 14, 0, 0),
    ligneRecette('contacts', 2, avecEcart ? 3_519 : 3_512, 3_377, 41, 64, 30, 0, 5),
    ligneRecette('factures-historiques', 1, 642, 642, 0, 0, 0, 0, 0),
  ];
}

const CONTROLES_DEMO: readonly ControleRecette[] = [
  'historique_localisations',
  'echantillon_fiches',
  'images_principales',
  'factures_anterieures',
];
const ETAPES_BASCULE_DEMO = [
  'controle',
  'scellement',
  'mandat',
  'purge',
  'invitations',
  'mise_en_service',
] as const;
/** Linéaire : la première bascule échoue à l'envoi des invitations, la reprise réussit. */
const DOSSIER_AUX_INVITATIONS_EN_ECHEC = 'OUV-2026-006';

const recettes = new Map<string, RecetteOuverture>();
const dejaEchoue = new Set<string>();

/** L'état de départ de la recette d'un dossier, selon l'endroit où il en est. */
function recetteInitiale(ligne: DossierOuverture): RecetteOuverture {
  const rang = ['recette', 'bascule_planifiee', 'en_service'].indexOf(ligne.etat);
  const validee = rang >= 1;
  const basculee = rang === 2;
  const direction = ligne.equipe.direction?.email ?? 'direction@galerie.test';
  const base = ligne.jalons.basculePrevueLe ?? jour(-10);
  const valideeLe = `${base}T08:00:00.000Z`;
  const cochees: ControleRecette[] =
    rang === -1
      ? []
      : rang === 0
        ? ['historique_localisations', 'images_principales']
        : [...CONTROLES_DEMO];
  const natures = rang === -1 ? naturesRecette(false).slice(0, 3) : naturesRecette(rang === 0);
  const enPlan = ligne.etat === 'reprise';
  return {
    dossierId: ligne.id,
    numero: ligne.numero,
    etat: ligne.etat,
    natures,
    aRevoir: natures.reduce((somme, nature) => somme + nature.aRevoir, 0),
    lots: {
      executes: natures.reduce((somme, nature) => somme + nature.lots, 0),
      annules: rang >= 0 ? 1 : 0,
      misDeCote:
        rang >= 0
          ? [
              {
                id: '66f2000000000000000000c1',
                nature: 'expositions',
                etat: 'essaye',
                motif: 'Export des expositions illisible : reprises à la main par la galerie.',
              },
            ]
          : [],
      enCours: enPlan
        ? [{ id: '66f2000000000000000000c2', nature: 'contacts', etat: 'en_cours' }]
        : [],
      nonLances:
        ligne.etat === 'provisionne'
          ? [{ id: '66f2000000000000000000c3', nature: 'expositions', etat: 'depose' }]
          : [],
      planActif: enPlan,
    },
    controles: CONTROLES_DEMO.map((cle) => ({
      cle,
      coche: cochees.includes(cle),
      par: cochees.includes(cle) ? OPERATEUR.email : null,
      le: cochees.includes(cle) ? valideeLe : null,
      note:
        cle === 'echantillon_fiches' && cochees.includes(cle)
          ? '10 fiches par nature, revues avec la galerie.'
          : '',
    })),
    validation: validee
      ? {
          par: direction,
          le: valideeLe,
          natures,
          controles: cochees,
          pv: {
            disponible: true,
            le: valideeLe,
            rangeAuCoffre: ligne.offre.modules.includes('coffre'),
          },
        }
      : null,
    derogation: null,
    signalements:
      rang >= 0
        ? [
            {
              texte:
                rang === 0
                  ? 'Il manque quatre œuvres dans le fonds importé, et trois œuvres de 2019 n’ont pas leur image principale.'
                  : 'Trois œuvres de 2019 n’ont pas leur image principale.',
              par: direction,
              le: rang === 0 ? `${jour(-2)}T16:20:00.000Z` : valideeLe,
            },
          ]
        : [],
    bascule: {
      prevueLe: ligne.jalons.basculePrevueLe,
      le: basculee ? valideeLe : null,
      effectueeLe: basculee ? ligne.jalons.basculeEffectueeLe : null,
      enCours: false,
      etapes: basculee
        ? ETAPES_BASCULE_DEMO.map((cle) => ({
            cle,
            le: valideeLe,
            note: cle === 'controle' ? '8 enregistrement(s) à revoir.' : '',
          }))
        : [],
      erreur: null,
    },
  };
}

function recetteDe(ligne: DossierOuverture): RecetteOuverture {
  let recette = recettes.get(ligne.id);
  if (recette === undefined) {
    recette = recetteInitiale(ligne);
    recettes.set(ligne.id, recette);
  }
  // Ce que le dossier porte : l'état et la date prévue changent hors de l'onglet.
  recette.etat = ligne.etat;
  recette.bascule.prevueLe = ligne.jalons.basculePrevueLe;
  return recette;
}

/** Les galeries des dossiers déjà créés existent dans la liste des galeries de la démo. */
let galeriesSemees = false;
function assurerGaleries(): void {
  if (galeriesSemees) return;
  galeriesSemees = true;
  for (const ligne of dossiers) {
    if (ligne.galleryId === null) continue;
    const { id } = demoApi.creerGalerie({
      slug: ligne.identite.slug,
      nom: ligne.identite.nomCommercial,
      palier: ligne.offre.palier,
      langue: ligne.identite.langue,
      direction: ligne.equipe.direction ?? { nom: '', email: '' },
      estTest: false,
    });
    ligne.galleryId = id;
  }
}

function trouver(id: string): DossierOuverture {
  const trouve = dossiers.find((ligne) => ligne.id === id);
  if (!trouve) throw new ApiError(404, 'ressource.introuvable', 'Dossier d’ouverture introuvable.');
  return trouve;
}

function refus(raison: string, message: string, details: Record<string, unknown> = {}): ApiError {
  return new ApiError(409, 'conflit.version', message, { raison, ...details });
}

function resume(ligne: DossierOuverture): ResumeOuverture {
  return {
    id: ligne.id,
    numero: ligne.numero,
    etat: ligne.etat,
    nomCommercial: ligne.identite.nomCommercial,
    slug: ligne.identite.slug,
    estTest: ligne.identite.estTest,
    galleryId: ligne.galleryId,
    responsable: ligne.responsable?.email ?? null,
    basculePrevueLe: ligne.jalons.basculePrevueLe,
    modifieLe: ligne.modifieLe,
  };
}

function appliquer(ligne: DossierOuverture, sections: SectionsOuverture): void {
  if (sections.identite) {
    ligne.identite = {
      ...ligne.identite,
      ...sections.identite,
      adresse: sections.identite.adresse
        ? { ligne2: '', ...sections.identite.adresse }
        : ligne.identite.adresse,
    };
  }
  if (sections.offre) {
    ligne.offre = {
      ...ligne.offre,
      ...sections.offre,
      forfaitReprise: { ...ligne.offre.forfaitReprise, ...sections.offre.forfaitReprise },
    };
  }
  if (sections.contrat) ligne.contrat = { ...ligne.contrat, ...sections.contrat };
  if (sections.equipe) ligne.equipe = { ...ligne.equipe, ...sections.equipe };
  if (sections.prereglages) ligne.prereglages = { ...ligne.prereglages, ...sections.prereglages };
  if (sections.jalons) ligne.jalons = { ...ligne.jalons, ...sections.jalons };
  ligne.revision += 1;
  ligne.modifieLe = new Date().toISOString();
  ligne.transitionsPossibles = transitionsPossibles(ligne);
}

function exigerRevision(ligne: DossierOuverture, revision: number): void {
  if (ligne.revision !== revision) {
    throw refus('revision_ouverture_perimee', 'Le dossier a changé entre-temps.', {
      revision: ligne.revision,
    });
  }
}

function exigerOuvert(ligne: DossierOuverture): void {
  if (CLOS.includes(ligne.etat)) {
    throw refus('dossier_clos', 'Ce dossier est clos : il ne se modifie plus.', {
      etat: ligne.etat,
    });
  }
}

function cocher(
  ligne: DossierOuverture,
  cle: EtapeCreationOuverture,
  etat: EtapeOuverture['etat'],
  note = '',
): void {
  ligne.etapes.push({ cle, etat, par: OPERATEUR.email, le: new Date().toISOString(), note });
  ligne.revision += 1;
}

/**
 * Cette galerie est celle d'un dossier d'ouverture de démonstration au-delà de
 * la provision : son contrat prévoit le mandat de reprise (connecteur Dropbox).
 */
export function galerieDeDossierEnReprise(idGalerie: string): boolean {
  return dossiers.some((ligne) => ligne.galleryId === idGalerie && ligne.etat !== 'brouillon');
}

export const demoOuvertures = {
  lister(filtres: { etat?: EtatOuverture; responsable?: string; page?: number }): PageOuvertures {
    assurerGaleries();
    const items = dossiers
      .filter((ligne) => filtres.etat === undefined || ligne.etat === filtres.etat)
      // Le compte de démonstration est le responsable de tous les dossiers.
      .filter(
        (ligne) =>
          filtres.responsable === undefined ||
          filtres.responsable === 'moi' ||
          filtres.responsable === ligne.responsable?.id,
      )
      .map(resume);
    return { items, page: filtres.page ?? 1, total: items.length };
  },

  obtenir(id: string): DossierOuverture {
    assurerGaleries();
    return structuredClone(trouver(id));
  },

  creer(corps: SectionsOuverture): DossierOuverture {
    const ligne = dossierVide(dossiers.length + 1, corps.identite?.nomCommercial ?? '');
    appliquer(ligne, corps);
    ligne.revision = 0;
    dossiers.push(ligne);
    return structuredClone(ligne);
  },

  modifier(id: string, corps: ModificationOuverture): DossierOuverture {
    const ligne = trouver(id);
    exigerOuvert(ligne);
    exigerRevision(ligne, corps.revision);
    if (corps.identite?.slug !== undefined && corps.identite.slug !== ligne.identite.slug) {
      if (ligne.galleryId !== null) {
        throw refus('slug_fige', 'La galerie est créée : son slug ne change plus.');
      }
    }
    appliquer(ligne, corps);
    if (corps.note) {
      ligne.notes.push({ texte: corps.note, par: OPERATEUR.email, le: new Date().toISOString() });
    }
    return structuredClone(ligne);
  },

  transition(
    id: string,
    corps: { vers: EtatOuverture; revision: number; motif?: string },
  ): ReponseTransitionOuverture {
    const ligne = trouver(id);
    exigerRevision(ligne, corps.revision);
    if (corps.vers === 'en_service') {
      throw refus('bascule_requise', 'La mise en service ne passe que par la bascule.');
    }
    if (!ligne.transitionsPossibles.includes(corps.vers)) {
      throw refus('transition_interdite', 'Transition interdite depuis cet état.', {
        de: ligne.etat,
        vers: corps.vers,
        possibles: ligne.transitionsPossibles,
      });
    }
    if (corps.vers === 'contrat_signe' && (!ligne.contrat.reference || !ligne.contrat.signeLe)) {
      throw refus('contrat_incomplet', 'Indiquez la référence et la date de signature.');
    }
    if (corps.vers === 'provisionne') {
      const faites = new Set(
        ligne.etapes
          .filter((etape) => etape.etat === 'fait' || etape.etat === 'sans_objet')
          .map((etape) => etape.cle),
      );
      const restantes = ETAPES.filter(
        (cle) => cle !== 'invitations' && cle !== 'prereglages' && !faites.has(cle),
      );
      if (ligne.galleryId === null || restantes.length > 0) {
        throw refus(
          'provisionnement_incomplet',
          'La galerie n’est pas encore entièrement créée : terminez la création.',
          { restantes },
        );
      }
    }
    if (corps.vers === 'recette') {
      if (ligne.galleryId === null) {
        throw refus('galerie_absente', 'Ce dossier n’a pas encore de galerie : rien à recetter.');
      }
      const { lots } = recetteDe(ligne);
      if (lots.planActif) {
        throw refus(
          'reprise_en_cours',
          'Un plan ou un travail de reprise occupe encore la galerie : attendez qu’il se termine.',
        );
      }
      if (lots.enCours.length > 0) {
        throw refus('lots_non_termines', 'Des lots sont lancés sans être terminés.', {
          lots: lots.enCours,
        });
      }
      if (lots.nonLances.length > 0) {
        const motif = corps.motif?.trim() ?? '';
        if (motif === '') {
          throw refus('lots_non_termines', 'Des lots n’ont jamais été lancés.', {
            lots: lots.nonLances,
          });
        }
        lots.misDeCote.push(...lots.nonLances.map((lot) => ({ ...lot, motif })));
        lots.nonLances = [];
      }
    }
    if (corps.vers === 'bascule_planifiee') {
      const recette = recetteDe(ligne);
      if (recette.validation === null && recette.derogation === null) {
        throw refus(
          'validation_requise',
          'La galerie n’a pas validé la reprise : attendez sa validation, ou une dérogation de la direction de Venio.',
        );
      }
      if (ligne.jalons.basculePrevueLe === null) {
        throw refus('bascule_non_datee', 'Indiquez la date de bascule prévue.', {
          manquants: ['jalons.basculePrevueLe'],
        });
      }
    }
    ligne.etat = corps.vers;
    ligne.revision += 1;
    ligne.modifieLe = new Date().toISOString();
    ligne.transitionsPossibles = transitionsPossibles(ligne);
    return {
      ...structuredClone(ligne),
      ...(corps.vers === 'abandonne' && ligne.galleryId !== null
        ? { resiliation: { aFaire: true as const, galleryId: ligne.galleryId } }
        : {}),
    };
  },

  deposerContrat(id: string, octets: number): DossierOuverture {
    const ligne = trouver(id);
    exigerOuvert(ligne);
    ligne.contrat.piece = {
      sha256: '0d1e2f30415263748596a7b8c9d0e1f20d1e2f30415263748596a7b8c9d0e1f2',
      octets,
      deposeLe: new Date().toISOString(),
      par: 'direction@venio.test',
    };
    ligne.revision += 1;
    return structuredClone(ligne);
  },

  creerGalerie(id: string): ReponseCreationGalerieOuverture {
    const ligne = trouver(id);
    const permis =
      ligne.etat === 'brouillon'
        ? ligne.identite.estTest
        : ['contrat_signe', 'provisionne', 'reprise'].includes(ligne.etat);
    if (!permis) {
      throw refus(
        'etat_incompatible',
        'La galerie se crée une fois le contrat signé (ou d’emblée pour un espace de test).',
        { etat: ligne.etat },
      );
    }
    const manquants = [
      ...(/^[a-z0-9]+(-[a-z0-9]+)*$/.test(ligne.identite.slug) ? [] : ['identite.slug']),
      ...(ligne.identite.nomCommercial.trim() === '' ? ['identite.nomCommercial'] : []),
      ...(ligne.equipe.direction === null ? ['equipe.direction'] : []),
    ];
    if (manquants.length > 0) {
      throw refus('dossier_incomplet', 'Le dossier ne suffit pas à créer la galerie.', {
        manquants,
      });
    }

    assurerGaleries();
    const faites: EtapeCreationOuverture[] = [];
    const faite = (cle: EtapeCreationOuverture): boolean =>
      ligne.etapes.some((etape) => etape.cle === cle);
    const direction = ligne.equipe.direction!;
    let invitationsEnvoyees = 0;

    if (!faite('provisionnement')) {
      const cree = demoApi.creerGalerie({
        slug: ligne.identite.slug,
        nom: ligne.identite.nomCommercial,
        palier: ligne.offre.palier,
        langue: ligne.identite.langue,
        direction,
        estTest: ligne.identite.estTest,
      });
      ligne.galleryId = cree.id;
      cocher(ligne, 'provisionnement', 'fait');
      faites.push('provisionnement');
      if (ligne.equipe.invitations === 'maintenant') invitationsEnvoyees += 1;
    }
    const sousEtapes: Array<[EtapeCreationOuverture, EtapeOuverture['etat'], string]> = [
      [
        'modules',
        ligne.offre.modules.length === 0 ? 'sans_objet' : 'fait',
        ligne.offre.modules.join(', '),
      ],
      ['addons', ligne.offre.addons.length === 0 ? 'sans_objet' : 'fait', ''],
      ['domaine', ligne.prereglages.domaine === null ? 'sans_objet' : 'fait', ''],
      [
        'invitations',
        ligne.equipe.membres.length === 0
          ? 'sans_objet'
          : ligne.equipe.invitations === 'maintenant'
            ? 'fait'
            : 'en_cours',
        '',
      ],
      [
        'prereglages',
        ligne.identite.raisonSociale !== '' || ligne.prereglages.mentionsComplementaires.length > 0
          ? 'en_cours'
          : 'sans_objet',
        '',
      ],
    ];
    for (const [cle, etat, note] of sousEtapes) {
      if (faite(cle)) continue;
      cocher(ligne, cle, etat, note);
      faites.push(cle);
      if (cle === 'invitations' && etat === 'fait')
        invitationsEnvoyees += ligne.equipe.membres.length;
    }
    ligne.modifieLe = new Date().toISOString();
    ligne.transitionsPossibles = transitionsPossibles(ligne);
    return { ...structuredClone(ligne), faites, invitationsEnvoyees };
  },

  recette(id: string): RecetteOuverture {
    assurerGaleries();
    const ligne = trouver(id);
    if (ligne.galleryId === null) {
      throw refus('galerie_absente', 'Ce dossier n’a pas encore de galerie : rien à recetter.');
    }
    return structuredClone(recetteDe(ligne));
  },

  cocherControle(
    id: string,
    corps: { controle: ControleRecette; coche: boolean; note?: string },
  ): RecetteOuverture {
    assurerGaleries();
    const ligne = trouver(id);
    if (!['reprise', 'recette', 'bascule_planifiee'].includes(ligne.etat)) {
      throw refus(
        'etat_incompatible',
        'La liste de contrôle se coche pendant la reprise, la recette et avant la bascule.',
        { etat: ligne.etat },
      );
    }
    const recette = recetteDe(ligne);
    recette.controles = recette.controles.map((controle) =>
      controle.cle !== corps.controle
        ? controle
        : corps.coche
          ? {
              ...controle,
              coche: true,
              par: OPERATEUR.email,
              le: new Date().toISOString(),
              note: corps.note ?? '',
            }
          : { ...controle, coche: false, par: null, le: null, note: '' },
    );
    ligne.revision += 1;
    return structuredClone(recette);
  },

  deroger(id: string, motif: string): RecetteOuverture {
    assurerGaleries();
    const ligne = trouver(id);
    const recette = recetteDe(ligne);
    if (recette.derogation !== null) return structuredClone(recette);
    if (ligne.etat !== 'recette') {
      throw refus('etat_incompatible', 'Une dérogation se pose pendant la recette.', {
        etat: ligne.etat,
      });
    }
    recette.derogation = { motif, par: OPERATEUR.email, le: new Date().toISOString() };
    ligne.revision += 1;
    return structuredClone(recette);
  },

  basculer(id: string): ReponseBascule {
    assurerGaleries();
    const ligne = trouver(id);
    const recette = recetteDe(ligne);
    const rendre = (faites: EtapeBascule[], invitationsEnvoyees: number): ReponseBascule => ({
      ...structuredClone(ligne),
      faites,
      invitationsEnvoyees,
      aRevoir: recette.aRevoir,
    });
    if (ligne.etat === 'en_service') return rendre([], 0);
    if (ligne.etat !== 'bascule_planifiee') {
      throw refus('etat_incompatible', 'Seul un dossier dont la bascule est planifiée bascule.', {
        etat: ligne.etat,
      });
    }
    const { bascule } = recette;
    const maintenant = new Date();
    bascule.le ??= maintenant.toISOString();
    const faites: EtapeBascule[] = [];
    const fait = (cle: EtapeBascule, note = ''): void => {
      if (bascule.etapes.some((etape) => etape.cle === cle)) return;
      bascule.etapes.push({ cle, le: new Date().toISOString(), note });
      faites.push(cle);
    };
    fait('controle', `${String(recette.aRevoir)} enregistrement(s) à revoir.`);
    fait('scellement');
    fait('mandat');
    fait('purge');
    if (ligne.numero === DOSSIER_AUX_INVITATIONS_EN_ECHEC && !dejaEchoue.has(ligne.id)) {
      dejaEchoue.add(ligne.id);
      const message =
        '2 invitation(s) ne sont pas parties : relancez la bascule pour les renvoyer.';
      bascule.erreur = {
        etape: 'invitations',
        raison: 'invitations_non_envoyees',
        message,
        le: new Date().toISOString(),
      };
      throw new ApiError(503, 'interne.inattendue', message, {
        raison: 'invitations_non_envoyees',
      });
    }
    const invitations = 1 + ligne.equipe.membres.length;
    fait('invitations', `${String(invitations)} invitation(s) envoyée(s).`);
    fait('mise_en_service');
    bascule.erreur = null;
    bascule.effectueeLe = bascule.le;
    const apres = new Date(bascule.le).getTime() + 30 * 86_400_000;
    ligne.etat = 'en_service';
    ligne.jalons = {
      ...ligne.jalons,
      basculeEffectueeLe: bascule.le,
      scelleLe: ligne.jalons.scelleLe ?? bascule.le,
      purgeSourcesLe: new Date(apres).toISOString(),
    };
    ligne.revision += 1;
    ligne.modifieLe = new Date().toISOString();
    ligne.transitionsPossibles = transitionsPossibles(ligne);
    recette.etat = ligne.etat;
    return rendre(faites, invitations);
  },

  slugDisponible(slug: string): DisponibiliteSlug {
    if (!/^(?=[a-z0-9-]{3,40}$)[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
      return { disponible: false, raison: 'invalide' };
    }
    if (RESERVES.includes(slug)) return { disponible: false, raison: 'reserve' };
    if (
      demoApi.listerGaleries().some((galerie) => galerie.slug === slug) ||
      dossiers.some((ligne) => ligne.galleryId !== null && ligne.identite.slug === slug)
    ) {
      return { disponible: false, raison: 'pris' };
    }
    return { disponible: true };
  },
};
